import { useCallback, useEffect, useMemo, useState } from "react";
import { ClipboardList, Search } from "lucide-react";
import { CarregandoLista } from "../../../components/carregando";
import { Input } from "../../../components/ui/input";
import { fetchEventsLog } from "../api";
import { GROUP_STYLE, eventGroup, eventLabel } from "../eventLabels";
import EventoRow from "./EventoRow";

/** Tudo que o iFood mandou, traduzido: pedido, o que significa, origem, latência
 *  e situação. Cor da linha pelo tipo. Sempre ao vivo (5 s) enquanto a aba está
 *  aberta — regra do dono, 16/09: sem switch nem botão de atualizar. */
export default function EventosTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [atualizadoEm, setAtualizadoEm] = useState(null);
  const [q, setQ] = useState("");
  const [grupo, setGrupo] = useState("");

  const load = useCallback(async () => {
    try { setEvents(await fetchEventsLog(150)); setAtualizadoEm(new Date()); } catch { /* mantém */ } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 5000);
    return () => clearInterval(id);
  }, [load]);

  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    return events.filter((ev) => {
      if (grupo && eventGroup(ev.full_code) !== grupo) return false;
      if (!t) return true;
      return [ev.pedido_codigo, ev.ifood_display_id, ev.cliente_nome, ev.full_code, eventLabel(ev.full_code), ev.detalhe, ev.order_id]
        .some((v) => String(v || "").toLowerCase().includes(t));
    });
  }, [events, q, grupo]);

  const contagem = useMemo(() => events.reduce((acc, ev) => { const g = eventGroup(ev.full_code); acc[g] = (acc[g] || 0) + 1; return acc; }, {}), [events]);

  if (loading) return <CarregandoLista linhas={8} colunas={2} />; // 1ª carga; o "ao vivo" de 5 s não volta aqui

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold">Eventos do iFood</h3>
          <p className="text-sm text-muted-foreground">Cada aviso que o iFood mandou, o pedido a que pertence e o que o Núcleo fez</p>
        </div>
        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full bg-green-500/10 text-green-700 dark:text-green-400" title="Atualiza sozinho a cada 5 s enquanto esta aba estiver aberta">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
          Ao vivo{atualizadoEm ? ` · ${atualizadoEm.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}` : ""}
        </span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input id="ifood-eventos-busca" placeholder="Pedido, cliente, evento…" value={q} onChange={(e) => setQ(e.target.value)} className="h-8 w-60 pl-8 text-xs" />
        </div>
        <button onClick={() => setGrupo("")} className={`text-[11px] px-2 py-1 rounded-full border ${!grupo ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:text-foreground"}`}>
          Todos ({events.length})
        </button>
        {Object.entries(GROUP_STYLE).filter(([g]) => contagem[g]).map(([g, st]) => (
          <button key={g} onClick={() => setGrupo(grupo === g ? "" : g)}
            className={`text-[11px] px-2 py-1 rounded-full border inline-flex items-center gap-1.5 ${grupo === g ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:text-foreground"}`}>
            <span className={`w-2 h-2 rounded-full ${st.dot}`} /> {st.label} ({contagem[g]})
          </button>
        ))}
      </div>

      {filtrados.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>{events.length === 0 ? "Nenhum evento ainda" : "Nenhum evento com esse filtro"}</p>
          {events.length === 0 && <p className="text-xs mt-1">Aparecem aqui assim que a loja estiver conectada</p>}
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="p-3 font-semibold">Evento</th><th className="p-3 font-semibold">Pedido</th><th className="p-3 font-semibold">O que aconteceu</th><th className="p-3 font-semibold">Recebido</th><th className="p-3 font-semibold">Situação</th>
              </tr></thead>
              <tbody>{filtrados.map((ev, i) => <EventoRow key={ev.id || i} ev={ev} />)}</tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
